import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { listBrokerLinks } from "../services/brokerMirrorService";
import type { BrokerLinkSummary } from "../services/brokerMirrorService";

const DISMISS_KEY = "token_refresh_banner_dismissed";
const POLL_INTERVAL_MS = 5 * 60 * 1000; // Re-check every 5 minutes

/**
 * Global banner that appears when any broker link has token_expiring or token_expired status.
 * Shows on all pages. Dismissal resets when the problem links change.
 */
export function TokenRefreshBanner() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [problemLinks, setProblemLinks] = useState<BrokerLinkSummary[]>([]);
  const [dismissed, setDismissed] = useState(true); // Start dismissed to avoid flash
  const [loaded, setLoaded] = useState(false);

  const checkLinks = useCallback(() => {
    if (!user) return;

    listBrokerLinks()
      .then((links) => {
        const problems = links.filter(
          (l) => l.status === "token_expiring" || l.status === "token_expired",
        );
        setProblemLinks(problems);

        if (problems.length > 0) {
          const key = problems.map((l) => `${l.id}:${l.status}`).sort().join(",");
          const prevKey = sessionStorage.getItem(DISMISS_KEY);
          if (prevKey !== key) {
            setDismissed(false);
          } else {
            setDismissed(true);
          }
        } else {
          setDismissed(true);
        }
        setLoaded(true);
      })
      .catch(() => {
        // Silently ignore — user may not have brokerage access
        setLoaded(true);
      });
  }, [user]);

  useEffect(() => {
    if (!user) return;

    checkLinks();

    // Re-check periodically so banner appears if token expires while tab is open
    const interval = setInterval(checkLinks, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user?.id, checkLinks]);

  if (!loaded || problemLinks.length === 0 || dismissed) return null;

  const hasExpired = problemLinks.some((l) => l.status === "token_expired");
  const dismissKey = problemLinks.map((l) => `${l.id}:${l.status}`).sort().join(",");

  return (
    <div
      className={`${
        hasExpired
          ? "bg-red-600 dark:bg-red-500/10 border-b border-red-700 dark:border-red-500/30"
          : "bg-amber-600 dark:bg-amber-500/10 border-b border-amber-700 dark:border-amber-500/30"
      } px-4 py-2.5 flex items-center justify-between gap-3`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <svg
          className={`w-4 h-4 flex-shrink-0 ${
            hasExpired ? "text-white dark:text-red-400" : "text-white dark:text-amber-400"
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
        <span
          className={`text-xs sm:text-sm ${
            hasExpired ? "text-white dark:text-red-200" : "text-white dark:text-amber-200"
          }`}
        >
          {hasExpired
            ? `Schwab token expired — mirror trading is paused. `
            : `Schwab token expiring soon — re-authenticate to avoid interruption. `}
          <button
            type="button"
            onClick={() => navigate("/brokerage")}
            className={`underline font-medium ${
              hasExpired
                ? "text-white dark:text-red-100 hover:text-red-100 dark:hover:text-white"
                : "text-white dark:text-amber-100 hover:text-amber-100 dark:hover:text-white"
            }`}
          >
            Reconnect Now
          </button>
        </span>
      </div>
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          sessionStorage.setItem(DISMISS_KEY, dismissKey);
        }}
        className={`p-1 flex-shrink-0 ${
          hasExpired
            ? "text-white dark:text-red-400 hover:text-red-100 dark:hover:text-red-200"
            : "text-white dark:text-amber-400 hover:text-amber-100 dark:hover:text-amber-200"
        }`}
        aria-label="Dismiss"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
